import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import api from '../api/axios';
import { useAuth } from './AuthContext';
import { messaging, getToken } from '../firebase';

const NotificationContext = createContext(null);

const POLL_INTERVAL_MS = 30000; // 30 seconds

export function NotificationProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);
  const lastSeenIdsRef = useRef(new Set());
  const [newArrival, setNewArrival] = useState(null); // for toast trigger

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await api.get('/api/notifications?limit=30');
      const list = res.data.notifications || [];
      const count = res.data.unread_count || 0;

      // Detect new arrivals (IDs we haven't seen before)
      const previousIds = lastSeenIdsRef.current;
      if (previousIds.size > 0) {
        const newOnes = list.filter(
          (n) => !previousIds.has(n.id) && !n.read_at
        );
        if (newOnes.length > 0) {
          // Surface the most recent new one for toast display
          setNewArrival(newOnes[0]);
        }
      }
      lastSeenIdsRef.current = new Set(list.map((n) => n.id));

      setNotifications(list);
      setUnreadCount(count);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, [isAuthenticated]);

  const registerPushToken = async () => {
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;

    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
    });

    if (token) {
      await api.post('/api/notifications/tokens', {
        token,
        platform: 'web',
      });
    }
  } catch (err) {
    console.error('FCM token registration failed:', err);
  }
};
  // Initial load + polling
  useEffect(() => {
    if (!isAuthenticated) {
      setNotifications([]);
      setUnreadCount(0);
      lastSeenIdsRef.current = new Set();
      return;
    }

    setLoading(true);
    fetchNotifications().finally(() => setLoading(false));
    registerPushToken();

    pollRef.current = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [isAuthenticated, user?.id, fetchNotifications]);

  // Refetch when window regains focus (snappier UX)
  useEffect(() => {
    if (!isAuthenticated) return;
    const onFocus = () => fetchNotifications();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [isAuthenticated, fetchNotifications]);

    // WebSocket connection for real-time notifications
useEffect(() => {
  if (!isAuthenticated) return;

  const token = localStorage.getItem('token');
  if (!token) return;

  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  const wsBase = apiBase.replace(/^http/, 'ws');
  const wsUrl = `${wsBase}/ws?token=${encodeURIComponent(token)}`;

  let ws;
  let reconnectTimer;

  const connect = () => {
    ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      if (process.env.NODE_ENV === 'development') console.log('Notification WS connected');
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'notification:new' && msg.data) {
          ingestRealtime(msg.data);
        }
      } catch (err) {
        console.error('WS parse error:', err);
      }
    };

    ws.onclose = () => {
     if (process.env.NODE_ENV === 'development') console.log('Notification WS connected, reconnecting in 5s');
      reconnectTimer = setTimeout(connect, 5000);
    };

    ws.onerror = () => {
      ws.close();
    };
  };

  connect();

  return () => {
    clearTimeout(reconnectTimer);
    if (ws) {
      ws.onclose = null;
      ws.close();
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [isAuthenticated, user?.id]);

// Handle foreground FCM messages

  const markAsRead = async (id) => {
    try {
      await api.put(`/api/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id && !n.read_at
            ? { ...n, read_at: new Date().toISOString() }
            : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Mark as read failed:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/api/notifications/read-all');
      setNotifications((prev) =>
        prev.map((n) =>
          n.read_at ? n : { ...n, read_at: new Date().toISOString() }
        )
      );
      setUnreadCount(0);
    } catch (err) {
      console.error('Mark all as read failed:', err);
    }
  };

  const dismissToast = () => setNewArrival(null);

  // For Phase 2 (WebSocket): push a notification in directly without waiting for poll
  const ingestRealtime = (notif) => {
    if (!notif?.id) return;
    if (lastSeenIdsRef.current.has(notif.id)) return;
    lastSeenIdsRef.current.add(notif.id);
    setNotifications((prev) => [notif, ...prev]);
    if (!notif.read_at) setUnreadCount((c) => c + 1);
    setNewArrival(notif);
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        newArrival,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        dismissToast,
        ingestRealtime,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used inside NotificationProvider');
  }
  return ctx;
}